import React, { useState, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import GitBranchIcon from '@mui/icons-material/CallSplit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  List,
  Button,
  Tooltip
} from '@mui/material';
import { useCreateDevKg } from 'studio/components/CreateDevKg/CreateDevKg';
import { trackEvent } from 'utils/eventTracking';

import sx from './BranchSwitcher.styles';

const BranchSwitcher = ({ knowledgeGraphs, knowledgeGraph, user, fetchKnowledgeGraphs }) => {
  const history = useHistory();
  const location = useLocation();
  const [showBranchSwitcherModal, setShowBranchSwitcherModal] = useState(false);

  // Only show for super users or higher
  const canManageBranches = user?.isSuperUser || user?.isAdmin;

  const currentKgId = knowledgeGraph?.data?.id;

  // Determine if current branch is main or dev
  const isMainBranch = useMemo(() => {
    if (!knowledgeGraph?.data) return false;
    return !knowledgeGraph.data.developmentKgForName; // Main branches don't have this property
  }, [knowledgeGraph]);

  const mainKgId = useMemo(() => {
    if (!knowledgeGraph?.data) return null;

    return knowledgeGraph.data.developmentKgForName
      ? knowledgeGraphs?.find(kg => kg.data.info.default.name === knowledgeGraph.data.developmentKgForName)?.data.id
      : currentKgId;
  }, [knowledgeGraph, knowledgeGraphs, currentKgId]);

  const { createDevKnowledgeGraph, CreateDevKgModal } = useCreateDevKg(
    knowledgeGraph,
    fetchKnowledgeGraphs,
    user,
    mainKgId
  );

  const branches = useMemo(() => {
    if (!mainKgId || !knowledgeGraphs) return [];

    const mainKg = knowledgeGraphs.find(kg => kg.data.id === mainKgId);
    const devBranches =
      knowledgeGraphs.filter(kg => kg.data.developmentKgForName === mainKg?.data.info.default.name) || [];

    return [
      {
        id: mainKgId,
        name: 'Main',
        isMain: true,
        isActive: currentKgId === mainKgId
      },
      ...devBranches.map(kg => ({
        id: kg.data.id,
        name: kg.data.info.default.name,
        isMain: false,
        isActive: currentKgId === kg.data.id
      }))
    ];
  }, [knowledgeGraphs, mainKgId, currentKgId]);

  const currentBranch = branches.find(branch => branch.isActive);

  const openBranchSwitcher = () => {
    setShowBranchSwitcherModal(true);
  };

  const closeBranchSwitcher = () => {
    setShowBranchSwitcherModal(false);
  };

  const handleBranchSwitch = branchId => {
    if (branchId === 'create-new') {
      setShowBranchSwitcherModal(false);
      createDevKnowledgeGraph();
      return;
    }

    if (branchId !== currentKgId) {
      const targetBranch = branches.find(branch => branch.id === branchId);

      trackEvent('KG Branch Switched', {
        fromBranch: currentKgId,
        toBranch: branchId,
        fromType: isMainBranch ? 'main' : 'dev',
        toType: targetBranch?.isMain ? 'main' : 'dev',
        mainKgId,
        context: 'studio'
      });

      const params = new URLSearchParams(location.search);
      params.set('id', branchId);
      const newLocation = location.pathname + '?' + params.toString() + location.hash;
      history.push(newLocation);
      setShowBranchSwitcherModal(false);
    }
  };

  if (!canManageBranches || !knowledgeGraph?.data) {
    return null;
  }

  // If mainKgId is not found, it means the main KG for this dev branch is not accessible
  if (!mainKgId) {
    return null;
  }

  const tooltip = isMainBranch
    ? 'Select which branch to work on'
    : `This is a development branch based on '${knowledgeGraph.data.developmentKgForName}'. Switch between branches to work on different versions.`;

  return (
    <>
      <Tooltip title={tooltip} placement="right" arrow>
        <ListItem disablePadding>
          <ListItemButton
            sx={{
              ...sx.root,
              ...(isMainBranch ? sx.mainKg : sx.developmentKg)
            }}
            onClick={openBranchSwitcher}
          >
            <ListItemIcon>
              <GitBranchIcon
                sx={{
                  ...sx.gitIcon,
                  ...(isMainBranch ? sx.mainIcon : sx.developmentIcon)
                }}
              />
            </ListItemIcon>
            <ListItemText
              primary={currentBranch?.name || 'Main'}
              sx={isMainBranch ? sx.mainBranchText : sx.devBranchText}
            />
            <ListItemIcon>
              <ExpandMoreIcon sx={isMainBranch ? sx.mainArrow : sx.developmentArrow} />
            </ListItemIcon>
          </ListItemButton>
        </ListItem>
      </Tooltip>

      <Dialog fullWidth maxWidth="sm" sx={sx.dialog} open={showBranchSwitcherModal} onClose={closeBranchSwitcher}>
        <DialogTitle>
          Select a Branch
          <Button variant="contained" color="primary" onClick={() => handleBranchSwitch('create-new')} disabled={false}>
            Create new
          </Button>
        </DialogTitle>
        <List>
          {branches.map(branch => (
            <ListItem key={branch.id} disablePadding>
              <ListItemButton dense selected={branch.isActive} onClick={() => handleBranchSwitch(branch.id)}>
                <ListItemText primary={branch.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Dialog>

      {CreateDevKgModal()}
    </>
  );
};

export default BranchSwitcher;
